// src/pages/gene.codes/GeneticWheel/dashboardPanels/SnpEvidencePanel.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Tag, Typography } from "antd";

import { EmptyState, InlineLoading } from "../../../../components/shared";
import "./SnpEvidencePanel.css";
import ResultRow from "../../../research/ResultRow";
import LiteratureArticleDrawer from "../../../research/LiteratureArticleDrawer";
import { useLiteratureSearch } from "../../../../hooks/useResearch";
import type {
	FacetBucket,
	FilterField,
	LiteratureResult,
	LiteratureSearchParams,
	ResearchRecord,
} from "../../../../types/research/ResearchTypes";

const { Text, Link } = Typography;

const PAGE_SIZE = 20;
// Module-level so the query key is identity-stable across renders.
const FACET_FIELDS: FilterField[] = ["pub_types", "mesh_major"];

/** "Sikiric P, Seiwerth S, et al." — enough to recognise a paper, never a full author dump. */
const attributionOf = (r: LiteratureResult): string | null => {
	const authors = Array.isArray(r.meta.authors) ? r.meta.authors : [];
	if (authors.length === 0) return null;
	if (authors.length === 1) return authors[0];
	if (authors.length === 2) return `${authors[0]}, ${authors[1]}`;
	return `${authors[0]}, ${authors[1]}, et al.`;
};

/** "J Physiol · 2023 · PMID 12345678" */
const metaLineOf = (r: LiteratureResult): string | null => {
	const parts = [
		r.meta.journal ?? null,
		r.meta.pub_year != null ? String(r.meta.pub_year) : null,
		r.meta.pmid ? `PMID ${r.meta.pmid}` : null,
	].filter(Boolean) as string[];
	return parts.length > 0 ? parts.join(" · ") : null;
};

const toRecord = (r: LiteratureResult): ResearchRecord => ({
	...r,
	meta: r.meta as Record<string, unknown>,
	source_type: "literature",
	source_category: "medical_literature",
});

const httpStatusOf = (error: unknown): number | undefined => {
	const status = (error as { response?: { status?: number } } | null)?.response?.status;
	return typeof status === "number" ? status : undefined;
};

export interface SnpEvidencePanelProps {
	/** The selected SNP's rsID (`Staging.rs`). */
	rs?: string | null;
	/** Curated PubMed phrase (`Staging.searcher`), searched in preference to the rsID. */
	searcher?: string | null;

}

const SnpEvidencePanel: React.FC<SnpEvidencePanelProps> = ({ rs, searcher}) => {
	const term = searcher?.trim() || rs?.trim() || "";

	const [selectedType, setSelectedType] = useState<{ term: string; value: string | null }>({
		term: "",
		value: null,
	});
	const [chipTerm, setChipTerm] = useState("");
	const [chipBuckets, setChipBuckets] = useState<FacetBucket[]>([]);
	const [openRecord, setOpenRecord] = useState<ResearchRecord | null>(null);
	const [drawerOpen, setDrawerOpen] = useState(false);

	// Stamped with the term it belongs to and resolved during render — an effect would leave the
	// previous SNP's filter on the query key for one pass and buy a search nobody asked for.
	const activeType = selectedType.term === term ? selectedType.value : null;

	const params = useMemo<LiteratureSearchParams>(
		() => ({
			q: term,
			sort: "relevance",
			size: PAGE_SIZE,
			facets: FACET_FIELDS,
			record: false,
			...(activeType ? { filters: [{ field: "pub_types" as FilterField, value: activeType }] } : {}),
		}),
		[term, activeType],
	);

	const { data, isLoading, isError, error, isPlaceholderData } = useLiteratureSearch(params);

	// A filtered response re-scopes its own buckets to the filtered set, which would strand the
	// reader on one chip with no way back — so the row is pinned to the last unfiltered response.
	useEffect(() => {
		if (activeType || isPlaceholderData || !data?.available) return;
		setChipTerm(term);
		setChipBuckets(data.facets?.pub_types ?? []);
	}, [data, activeType, isPlaceholderData, term]);

	const chips = chipTerm === term ? chipBuckets : [];
	const results = data?.available ? data.data : [];

	const toggleType = (value: string) =>
		setSelectedType({ term, value: activeType === value ? null : value });

	const openArticle = (r: LiteratureResult) => {
		setOpenRecord(toRecord(r));
		setDrawerOpen(true);
	};

	const body = (() => {
		if (!term) {
			return <EmptyState title="No search term" description="This SNP has no rsID or search phrase to look up." />;
		}
		if (isLoading) return <InlineLoading message="Searching published evidence…" />;
		if (isError) {
			const forbidden = [401, 403].includes(httpStatusOf(error) ?? 0);
			return (
				<div className="snp-evidence__notice">
					<Alert
						type="warning"
						showIcon
						message={forbidden ? "Research evidence needs practitioner access" : "Research evidence unavailable"}
						description={
							forbidden
								? "This account can open the genetic wheel but not the literature library — ask an administrator for practitioner access."
								: "The literature search did not respond. Try again shortly."
						}
						data-testid="snp-evidence-error"
					/>
				</div>
			);
		}
		// A degraded connector is a normal result — surface the notice, never blank the panel.
		if (data && data.available === false) {
			return (
				<div className="snp-evidence__notice">
					<Alert
						type="warning"
						showIcon
						message="Research evidence unavailable"
						description={data.message || "The medical-literature connector is not configured for this clinic."}
						data-testid="snp-evidence-unavailable"
					/>
				</div>
			);
		}
		if (results.length === 0) {
			return <EmptyState title="No published evidence" description={`No literature matches ${term}.`} />;
		}

		return results.map((r) => (
			<ResultRow
				key={r.id}
				title={r.title}
				attribution={attributionOf(r)}
				meta={metaLineOf(r)}
				snippet={r.snippet}
				selected={openRecord?.id === r.id}
				onClick={() => openArticle(r)}
				data-testid={`snp-evidence-row-${r.id}`}

			/>
		));
	})();

	return (
		<div data-testid="snp-evidence-panel">
			{chips.length > 0 && (
				<div className="snp-evidence__chips" data-testid="snp-evidence-chips">
					{chips.map((b) => {
						const active = activeType === b.value;
						return (
							<Tag
								key={b.value}
								role="button"
								tabIndex={0}
								aria-pressed={active}
								onClick={() => toggleType(b.value)}
								onKeyDown={(e: React.KeyboardEvent) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										toggleType(b.value);
									}
								}}
								data-testid={`snp-evidence-chip-${b.value}`}
								className="snp-evidence__chip"
							>
								{b.value}{" "}
								<span className="snp-evidence__chip-count">{b.count.toLocaleString()}</span>
							</Tag>
						);
					})}
				</div>
			)}

			{body}

			{data?.available && results.length > 0 && (
				<Text
					type="secondary"
					className="snp-evidence__total"
					data-testid="snp-evidence-total"
				>
					{results.length} of {data.total.toLocaleString()} publications for {term}
				</Text>
			)}

			<LiteratureArticleDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} record={openRecord} />
		</div>
	);
};

export default SnpEvidencePanel;
